/**
 * Beider-Morse Phonetic Matching Algorithm Implementation in JavaScript
 * 
 * This algorithm creates phonetic encodings of names that take into account
 * various linguistic origins and pronunciation patterns. It's particularly
 * useful for genealogical research and multilingual name matching.
 * 
 * This JavaScript implementation matches the PHP implementation used in the MaterialMatrix application.
 */
export class BeiderMorse {
    /**
     * Constructor - initializes all rules
     */
    constructor() {
        // Language detection rules
        this.languageRules = {
            'english': [/th/, /^w/, /que$/, /^k(?!n)/, /ly$/],
            'german': [/^[sz]ch/, /[äöü]/, /[ei][sz]$/, /sch/],
            'french': [/^h?ou/, /[éèêë]/, /eau/, /[gq]u[ei]/],
            'spanish': [/^[jñ]/, /[aeio]ll[aeio]/, /[aeio]ñ[aeio]/, /^ll/],
            'italian': [/[aeiou]zz[aeiou]/, /[cgn]li[aeiou]/, /^gli/, /etta$/],
            'portuguese': [/^lh/, /nh/, /ção/, /[ãõ]/],
            'polish': [/cz/, /sz/, /rz/, /[łż]/, /^w/],
            'russian': [/^zh/, /tsya$/, /ya$/, /[ïÿ]/],
            'hebrew': [/^tz/, /^[jy]o/, /kh/, /^ch/]
        };

        // Initialize phonetic rules
        this.initializeRules();
    }

    /**
     * Initialize the phonetic transformation rules for each supported language
     */
    initializeRules() {
        // Common rules across languages
        this.commonRules = [
            // Vowels
            { pattern: /a/gu, replacement: ['a', 'o'] },
            { pattern: /e/gu, replacement: ['e', 'i'] },
            { pattern: /i/gu, replacement: ['i', 'e', 'y'] },
            { pattern: /o/gu, replacement: ['o', 'a'] },
            { pattern: /u/gu, replacement: ['u', 'o'] },

            // Common consonant variations
            { pattern: /b/gu, replacement: ['b', 'p'] },
            { pattern: /d/gu, replacement: ['d', 't'] },
            { pattern: /f/gu, replacement: ['f', 'p'] },
            { pattern: /g/gu, replacement: ['g', 'k'] },
            { pattern: /h/gu, replacement: ['h', ''] },
            { pattern: /j/gu, replacement: ['j', 'zh', 'dj'] },
            { pattern: /k/gu, replacement: ['k', 'g', 'q'] },
            { pattern: /l/gu, replacement: ['l'] },
            { pattern: /m/gu, replacement: ['m'] },
            { pattern: /n/gu, replacement: ['n'] },
            { pattern: /p/gu, replacement: ['p', 'b'] },
            { pattern: /q/gu, replacement: ['k'] },
            { pattern: /r/gu, replacement: ['r'] },
            { pattern: /s/gu, replacement: ['s', 'z'] },
            { pattern: /t/gu, replacement: ['t', 'd'] },
            { pattern: /v/gu, replacement: ['v', 'f', 'b'] },
            { pattern: /w/gu, replacement: ['w', 'v'] },
            { pattern: /x/gu, replacement: ['ks', 'gz'] },
            { pattern: /y/gu, replacement: ['y', 'i'] },
            { pattern: /z/gu, replacement: ['z', 's'] }
        ];

        // Language-specific rules
        this.languageSpecificRules = {
            'english': [
                { pattern: /th/gu, replacement: ['t', 'th'] },
                { pattern: /ch/gu, replacement: ['tsh', 'k'] },
                { pattern: /tion/gu, replacement: ['shn', 'shun'] },
                { pattern: /^k(?!n)/gu, replacement: ['k', 'n'] },
                { pattern: /^wr/gu, replacement: ['r'] },
                { pattern: /^wh/gu, replacement: ['w', 'hw'] },
                { pattern: /^gn/gu, replacement: ['n', 'gn'] },
                { pattern: /mb$/gu, replacement: ['m'] },
                { pattern: /ck/gu, replacement: ['k'] },
                { pattern: /gh/gu, replacement: ['g', '', 'f'] }
            ],
            'german': [
                { pattern: /ä/gu, replacement: ['e', 'ae'] },
                { pattern: /ö/gu, replacement: ['e', 'oe'] },
                { pattern: /ü/gu, replacement: ['i', 'ue'] },
                { pattern: /sch/gu, replacement: ['sh'] },
                { pattern: /tsch/gu, replacement: ['tsh'] },
                { pattern: /ß/gu, replacement: ['s'] },
                { pattern: /^ch/gu, replacement: ['kh', 'h'] },
                { pattern: /ei/gu, replacement: ['ay'] },
                { pattern: /ie/gu, replacement: ['i'] },
                { pattern: /eu/gu, replacement: ['oy'] }
            ]
            // Additional language rules can be added here
        };
    }

    /**
     * Detect the likely language(s) of a name
     * 
     * @param {string} name - Name to analyze
     * @return {Array} Array of likely languages
     */
    detectLanguages(name) {
        name = name.toLowerCase();
        const languages = [];

        for (const language in this.languageRules) {
            let matches = false;
            for (const pattern of this.languageRules[language]) {
                if (pattern.test(name)) {
                    matches = true;
                    break;
                }
            }

            if (matches) {
                languages.push(language);
            }
        }

        // If no specific language detected, consider it could be any
        return languages.length > 0 ? languages : Object.keys(this.languageRules);
    }

    /**
     * Generate phonetic encodings for a name
     * 
     * @param {string} name - Name to encode
     * @param {number} maxEncodings - Maximum number of encodings to return (default 10)
     * @return {Array} Array of possible phonetic encodings
     */
    encode(name, maxEncodings = 10) {
        // Normalize: lowercase, remove non-alphabetic characters
        name = name.toLowerCase();
        name = name.replace(/[^a-zäöüßéèêëçñłżáíóúãõ]/gu, '');

        if (!name) {
            return [''];
        }

        // For material names, we'll limit to just a few languages most common for stone names
        // This significantly improves performance
        const limitedLanguages = ['english', 'italian', 'spanish', 'portuguese'];

        // Detect likely languages but limit to our preferred set
        let detectedLanguages = this.detectLanguages(name);
        detectedLanguages = detectedLanguages.filter(lang => limitedLanguages.includes(lang));

        // If no matches in our limited set, use the limited set
        if (detectedLanguages.length === 0) {
            detectedLanguages = limitedLanguages;
        }

        // Generate encodings for each detected language
        let allEncodings = [];
        let encodingsCount = 0;

        for (const language of detectedLanguages) {
            // Only process if we haven't reached our maximum yet
            if (encodingsCount >= maxEncodings) {
                break;
            }

            const languageEncodings = this.encodeForLanguage(name, language);

            // Limit the number of encodings per language
            const limitedEncodings = languageEncodings.slice(0, maxEncodings - encodingsCount);
            encodingsCount += limitedEncodings.length;

            allEncodings = allEncodings.concat(limitedEncodings);
        }

        // Remove duplicates and sort
        allEncodings = [...new Set(allEncodings)];
        allEncodings.sort();

        return allEncodings;
    }

    /**
     * Generate phonetic encodings for a name in a specific language
     * 
     * @param {string} name - Name to encode
     * @param {string} language - Language to use for encoding
     * @return {Array} Array of possible phonetic encodings for the language
     */
    encodeForLanguage(name, language) {
        let encodings = [name];

        // Apply language-specific rules first
        if (this.languageSpecificRules[language]) {
            encodings = this.applyRules(encodings, this.languageSpecificRules[language]);
        }

        // Then apply common rules
        encodings = this.applyRules(encodings, this.commonRules);

        // Process double letters
        encodings = encodings.map(encoding => this.handleDoubleLetters(encoding));

        // Remove duplicates
        return [...new Set(encodings)];
    }

    /**
     * Apply phonetic rules to a set of encodings
     * 
     * @param {Array} encodings - Current set of encodings
     * @param {Array} rules - Rules to apply
     * @param {number} maxResults - Maximum number of results to return (default 50)
     * @return {Array} New set of encodings after rules applied
     */
    applyRules(encodings, rules, maxResults = 50) {
        let newEncodings = [...encodings];

        for (const rule of rules) {
            // If we already have too many encodings, stop applying rules
            if (newEncodings.length > maxResults) {
                newEncodings = newEncodings.slice(0, maxResults);
                break;
            }

            const tempEncodings = [];

            for (const encoding of newEncodings) {
                // For each current encoding, apply the rule in all possible ways
                if (!rule.pattern.test(encoding)) {
                    // If no match, keep the encoding unchanged
                    tempEncodings.push(encoding);
                    continue;
                }

                // Reset the regex lastIndex to ensure proper matching
                rule.pattern.lastIndex = 0;

                // Generate variations based on the rule
                for (const replacement of rule.replacement) {
                    // Reset the regex lastIndex again for each replacement
                    rule.pattern.lastIndex = 0;

                    const newEncoding = encoding.replace(rule.pattern, replacement);
                    tempEncodings.push(newEncoding);

                    // If we've reached our limit, stop generating more
                    if (tempEncodings.length >= maxResults) {
                        break;
                    }
                }

                // If we've reached our limit, stop processing more encodings
                if (tempEncodings.length >= maxResults) {
                    break;
                }
            }

            newEncodings = tempEncodings;
        }

        return newEncodings;
    }

    /**
     * Handle double letters in encodings
     * 
     * @param {string} encoding - Encoding to process
     * @return {string} Processed encoding
     */
    handleDoubleLetters(encoding) {
        // Replace double letters with singles
        return encoding.replace(/(.)\1+/gu, '$1');
    }

    /**
     * Compare two names and determine if they match phonetically
     * 
     * @param {string} name1 - First name to compare
     * @param {string} name2 - Second name to compare
     * @param {number} threshold - Similarity threshold (0.0 to 1.0, default 0.8)
     * @return {boolean} True if names match phonetically
     */
    isMatch(name1, name2, threshold = 0.8) {
        // If names are identical, return true immediately
        if (name1.toLowerCase().trim() === name2.toLowerCase().trim()) {
            return true;
        }

        // For material names, we'll use a more flexible matching approach
        // First, try direct encoding match
        const encodings1 = this.encode(name1);
        const encodings2 = this.encode(name2);

        // Check for any common encodings
        for (const encoding1 of encodings1) {
            for (const encoding2 of encodings2) {
                if (encoding1 === encoding2) {
                    return true;
                }
            }
        }

        // If no direct match, try similarity-based matching
        // This is especially useful for material names with slight variations
        const name1Clean = this.cleanNameForComparison(name1);
        const name2Clean = this.cleanNameForComparison(name2);

        // Check for similarity using Levenshtein distance
        const maxLength = Math.max(name1Clean.length, name2Clean.length);
        if (maxLength > 0) {
            const levenshtein = this.levenshteinDistance(name1Clean, name2Clean);
            const similarity = 1 - (levenshtein / maxLength);

            if (similarity >= threshold) {
                return true;
            }
        }

        // Also check individual words for material names with multiple words
        const words1 = name1Clean.split(' ').filter(word => word.length > 0);
        const words2 = name2Clean.split(' ').filter(word => word.length > 0);

        // If one name is a subset of the other (e.g., "Amazonite" vs "Amazonite Top")
        if (words1.length !== words2.length) {
            const shortWords = words1.length < words2.length ? words1 : words2;
            const longWords = words1.length < words2.length ? words2 : words1;

            let matchedWords = 0;
            for (const word of shortWords) {
                for (const longWord of longWords) {
                    if (word === longWord || this.levenshteinDistance(word, longWord) <= 2) {
                        matchedWords++;
                        break;
                    }
                }
            }

            if (matchedWords / shortWords.length >= threshold) {
                return true;
            }
        }

        return false;
    }

    /**
     * Get the first encoding of a name (useful for indexing)
     * 
     * @param {string} name - Name to encode
     * @return {string} First phonetic encoding
     */
    getFirstEncoding(name) {
        const encodings = this.encode(name);
        return encodings.length > 0 ? encodings[0] : '';
    }

    /**
     * Get all encodings as a space-separated string
     * 
     * @param {string} name - Name to encode
     * @return {string} Space-separated encodings
     */
    getEncodingsAsString(name) {
        return this.encode(name).join(' ');
    }

    /**
     * Clean a name for comparison by removing common words and normalizing
     * 
     * @param {string} name - Name to clean
     * @return {string} Cleaned name
     */
    cleanNameForComparison(name) {
        // Convert to lowercase
        name = name.toLowerCase();

        // Remove common material name suffixes and words that don't add phonetic value
        const wordsToRemove = ['top', 'slab', 'tile', 'stone', 'granite', 'marble', 'quartz', 'quartzite'];

        // Replace common spelling variations
        const replacements = {
            'calacatta': 'calcatta',
            'carrara': 'carrera',
            'marquina': 'markina',
            'venatto': 'venato',
            'emperador': 'emprador',
            'marfil': 'marphil',
            'blue': 'blu',
            'pearl': 'perl',
            'amazzonite': 'amazonite'
        };

        // Apply replacements
        for (const [search, replace] of Object.entries(replacements)) {
            name = name.replace(new RegExp(search, 'g'), replace);
        }

        // Remove words to ignore
        const words = name.split(' ');
        const filteredWords = words.filter(word => !wordsToRemove.includes(word));

        return filteredWords.join(' ');
    }

    /**
     * Calculate Levenshtein distance between two strings
     * 
     * @param {string} a - First string
     * @param {string} b - Second string
     * @return {number} Levenshtein distance
     */
    levenshteinDistance(a, b) {
        if (a.length === 0) return b.length;
        if (b.length === 0) return a.length;

        const matrix = [];

        // Initialize matrix
        for (let i = 0; i <= b.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= a.length; j++) {
            matrix[0][j] = j;
        }

        // Calculate distances
        for (let i = 1; i <= b.length; i++) {
            for (let j = 1; j <= a.length; j++) {
                if (b.charAt(i - 1) === a.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1, // substitution
                        matrix[i][j - 1] + 1,     // insertion
                        matrix[i - 1][j] + 1      // deletion
                    );
                }
            }
        }

        return matrix[b.length][a.length];
    }
}
